import { useEffect, useState } from "react";
import { Timer, TimerOff } from "lucide-react";

function diffParts(target) {
  const ms = target - Date.now();
  if (ms <= 0) return null;
  const s = Math.floor(ms / 1000);
  return {
    d: Math.floor(s / 86400),
    h: Math.floor((s % 86400) / 3600),
    m: Math.floor((s % 3600) / 60),
    s: s % 60,
    ms,
  };
}

// Countdown pill: shows time left to grab a gift. Turns urgent (accent + pulse) under 1h.
export const CountdownTimer = ({ expiresAt, className = "", size = "sm" }) => {
  const [parts, setParts] = useState(() => (expiresAt ? diffParts(new Date(expiresAt).getTime()) : null));
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!expiresAt) return;
    const target = new Date(expiresAt).getTime();
    const tick = () => {
      const p = diffParts(target);
      if (!p) { setExpired(true); setParts(null); return; }
      setExpired(false); setParts(p);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  if (!expiresAt) return null;

  const pad = (n) => String(n).padStart(2, "0");
  const base = `inline-flex items-center gap-1.5 rounded-full font-semibold tabular-nums ${size === "lg" ? "text-sm px-3.5 py-1.5" : "text-xs px-2.5 py-1"}`;

  if (expired || !parts) {
    return (
      <span className={`${base} bg-muted text-muted-foreground ${className}`} data-testid="countdown-expired">
        <TimerOff className={size === "lg" ? "h-4 w-4" : "h-3.5 w-3.5"} /> Scaduto
      </span>
    );
  }

  const urgent = parts.ms < 3600 * 1000; // under 1 hour
  let label;
  if (parts.d > 0) label = `${parts.d}g ${parts.h}h`;
  else if (parts.h > 0) label = `${parts.h}:${pad(parts.m)}:${pad(parts.s)}`;
  else label = `${pad(parts.m)}:${pad(parts.s)}`;

  const tone = urgent
    ? "bg-accent text-white animate-pulse"
    : "bg-white/90 text-foreground backdrop-blur-sm";

  return (
    <span className={`${base} ${tone} ${className}`} data-testid="countdown-timer">
      <Timer className={size === "lg" ? "h-4 w-4" : "h-3.5 w-3.5"} /> {label}
    </span>
  );
};

export default CountdownTimer;
